<script setup lang="ts">
import { FocusTrap } from '@headlessui/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import DatePickerPanel from './DatePickerPanel.vue'
import type {
  DatePickerChange,
  DatePickerCloseReason,
  DatePickerCommitIntent,
  DatePickerCommitScope,
  DatePickerProps,
  DateRangeValue,
} from './types'

import { useClickOutside } from '@/shared/composables/useClickOutside'
import { dt, useFormat } from '@/shared/lib/format'
import { useId } from '@/shared/ui/composables'
import { useFloating } from '@/shared/ui/floating'
import { Icon } from '@/shared/ui/icon'
import { FloatingLayer, OverlayPortal } from '@/shared/ui/overlay'

const props = withDefaults(defineProps<DatePickerProps>(), {
  hideCompare: false,
  disabled: false,
  startPlaceholder: '',
  endPlaceholder: '',
  compareStartPlaceholder: '',
  compareEndPlaceholder: '',
  hidePresetList: false,
  hideControlList: false,
  hideRightPanel: false,
  inputStyle: false,
  compact: false,
  selectablePresets: null,
  calendarHeight: '',
  embedded: false,
  applyAllLabel: '',
  showApplyAll: false,
  openReverse: false,
  compareOffLabel: '',
})

const model = defineModel<DateRangeValue>({
  default: (): DateRangeValue => ({
    start: null,
    end: null,
    presetId: null,
    compareEnabled: false,
    compareType: 'Previous Period',
    compareStart: null,
    compareEnd: null,
  }),
})

const emit = defineEmits<{
  open: []
  close: [reason: DatePickerCloseReason]
  reset: [value: DateRangeValue]
  commit: [intent: DatePickerCommitIntent]
}>()

const focusFeatures =
  FocusTrap.features.InitialFocus | FocusTrap.features.TabLock | FocusTrap.features.RestoreFocus
const panelId = useId('date-picker-panel')
const fmt = useFormat()
const isOpen = ref(false)
const reference = ref<HTMLElement | null>(null)
const floating = ref<HTMLElement | null>(null)

function cloneValue(value: DateRangeValue): DateRangeValue {
  return {
    ...value,
    start: value.start ? new Date(value.start) : null,
    end: value.end ? new Date(value.end) : null,
    compareStart: value.compareStart ? new Date(value.compareStart) : null,
    compareEnd: value.compareEnd ? new Date(value.compareEnd) : null,
  }
}

const draft = ref<DateRangeValue>(cloneValue(model.value))
const pristine = ref<DateRangeValue>(cloneValue(model.value))
const activePresetLabel = computed(
  () => props.presets.find((preset) => preset.id === draft.value.presetId)?.label ?? '',
)
const activeCompareLabel = computed(
  () =>
    props.compareModes.find((mode) => mode.id === draft.value.compareType)?.label ??
    draft.value.compareType,
)

function displayRange(start: Date | null, end: Date | null): string {
  if (!start && !end) return props.emptyLabel
  if (!start) return dt.formatDate(end, fmt.locale)
  if (!end) return dt.formatDate(start, fmt.locale)

  const sameYear = start.getFullYear() === end.getFullYear()
  const sameDay =
    sameYear && start.getMonth() === end.getMonth() && start.getDate() === end.getDate()
  if (sameDay) return dt.formatDate(start, fmt.locale)

  const left = dt.formatDate(start, fmt.locale, { omitYear: sameYear })
  const right = dt.formatDate(end, fmt.locale)
  return `${left}${props.rangeSeparator}${right}`
}

const primarySummary = computed(() => displayRange(draft.value.start, draft.value.end))
const compareSummary = computed(() =>
  draft.value.compareEnabled && (draft.value.compareStart || draft.value.compareEnd)
    ? displayRange(draft.value.compareStart, draft.value.compareEnd)
    : '',
)
const showControls = computed(
  () =>
    !props.hideRightPanel &&
    !props.hideControlList &&
    (!props.inputStyle || draft.value.presetId === 'Custom'),
)
const showFooter = computed(() => showControls.value || props.hideControlList)

const { floatingStyles } = useFloating(reference, floating, {
  placement: props.openReverse ? 'top-end' : 'bottom-end',
  offset: 6,
  strategy: 'fixed',
})

function restoreTriggerFocus(): void {
  window.setTimeout(() => reference.value?.focus(), 0)
}

function finishClose(reason: DatePickerCloseReason): void {
  isOpen.value = false
  emit('close', reason)
  restoreTriggerFocus()
}

function normaliseIncompleteRanges(value: DateRangeValue): DateRangeValue {
  const next = cloneValue(value)
  if (next.start && !next.end) next.end = new Date(next.start)
  if (
    next.compareEnabled &&
    next.compareType === 'Custom' &&
    next.compareStart &&
    !next.compareEnd
  ) {
    next.compareEnd = new Date(next.compareStart)
  }
  return next
}

function commit(scope: DatePickerCommitScope, reason: DatePickerCloseReason): void {
  const value = normaliseIncompleteRanges(draft.value)
  model.value = cloneValue(value)
  draft.value = cloneValue(value)
  pristine.value = cloneValue(value)
  emit('commit', { scope, value: cloneValue(value) })
  finishClose(reason)
}

function dismiss(reason: 'dismiss' | 'trigger'): void {
  if (!isOpen.value) return
  if (props.hideControlList) {
    commit('current', 'auto-apply')
    return
  }
  draft.value = cloneValue(pristine.value)
  finishClose(reason)
}

function openPicker(): void {
  if (props.disabled) return
  if (isOpen.value) {
    dismiss('trigger')
    return
  }
  pristine.value = cloneValue(model.value)
  draft.value = cloneValue(model.value)
  isOpen.value = true
  emit('open')
}

function resetDraft(): void {
  draft.value = cloneValue(pristine.value)
  emit('reset', cloneValue(pristine.value))
}

function handlePanelChange(change: DatePickerChange): void {
  if (props.hideRightPanel && change.reason === 'preset') commit('current', 'auto-apply')
}

function handleEscape(event: KeyboardEvent): void {
  if (
    event.key !== 'Escape' ||
    !isOpen.value ||
    !(document.activeElement instanceof HTMLElement) ||
    !floating.value?.contains(document.activeElement)
  ) {
    return
  }
  event.preventDefault()
  event.stopPropagation()
  dismiss('dismiss')
}

useClickOutside(floating, () => dismiss('dismiss'), { include: () => [reference.value] })
watch(
  model,
  (value) => {
    if (!isOpen.value) {
      draft.value = cloneValue(value)
      pristine.value = cloneValue(value)
    }
  },
  { deep: true },
)
watch(isOpen, async (open) => {
  if (open) await nextTick()
})
onMounted(() => window.addEventListener('keydown', handleEscape, true))
onBeforeUnmount(() => window.removeEventListener('keydown', handleEscape, true))

const isSummaryClosed = computed(() => props.hideControlList && !isOpen.value)

// Mirrors the old `.is-input-style`/`.is-compact`/`.is-summary…:not([aria-expanded])` cascade
// from DatePicker.css: the states are mutually exclusive in every real call site (no caller
// combines `compact` with `inputStyle`), so a single resolved bundle avoids two same-specificity
// Tailwind utilities racing for the same property.
const triggerSkinClass = computed(() => {
  if (isSummaryClosed.value) return 'min-h-auto p-0 rounded-md border-transparent'
  if (props.compact) return 'h-10 min-h-10 p-xs rounded-md border-primary-subtle'
  if (props.inputStyle) return 'min-h-12 p-xs rounded-sm border-border'
  return 'min-h-10 p-xs rounded-md border-primary-subtle'
})

// The summary-closed state kept its border transparent even while focused (only the focus ring
// shadow showed) — suppress the focus border override for that state only, same as the CSS did.
const triggerFocusBorderClass = computed(() =>
  isSummaryClosed.value ? '' : 'focus-visible:border-ring',
)

defineExpose({ openPicker })
</script>

<template>
  <div
    class="ui-date-picker inline-flex w-full font-sans"
    :class="{
      'is-input-style': inputStyle,
      'is-summary': hideControlList,
      'is-compact': compact,
    }"
  >
    <button
      ref="reference"
      type="button"
      class="ui-date-picker__trigger flex w-full items-center gap-xs outline-none bg-surface text-fg text-start cursor-pointer border disabled:opacity-50 disabled:cursor-not-allowed focus-visible:shadow-[0_0_0_var(--ui-ring-width)_var(--ui-color-ring)]"
      :class="[triggerSkinClass, triggerFocusBorderClass]"
      :aria-label="triggerLabel"
      :aria-expanded="isOpen"
      :aria-controls="panelId"
      :disabled="disabled"
      @click="openPicker"
    >
      <span
        v-if="hideControlList"
        class="ui-date-picker__summary ui-date-picker__summary--preset-only flex min-w-0 flex-1 flex-row flex-wrap gap-xs items-baseline text-sm leading-5"
      >
        <strong class="ui-date-picker__preset-only-label text-fg font-bold">{{
          activePresetLabel
        }}</strong>
        <span v-if="!isOpen" class="ui-date-picker__preset-only-compare text-muted">
          {{ compareSeparator }}{{ draft.compareEnabled ? activeCompareLabel : compareOffLabel }}
        </span>
      </span>
      <template v-else>
        <Icon
          class="ui-date-picker__icon flex-none text-primary-muted"
          name="calendar-days"
          :size="16"
        />
        <span
          class="ui-date-picker__summary flex min-w-0 flex-1"
          :class="compact ? 'flex-col flex-wrap  gap-x-2xs' : 'flex-col'"
        >
          <span
            v-if="activePresetLabel"
            class="ui-date-picker__preset-label overflow-hidden text-muted text-[0.625rem] tracking-[0.04em] text-ellipsis uppercase whitespace-nowrap"
            :class="{ 'basis-full': compact, 'leading-3': compact }"
            >{{ activePresetLabel }}</span
          >
          <span class="flex gap-1">
            <span
              class="ui-date-picker__range text-xs font-semibold w-max block text-nowrap"
              :class="{ 'leading-3': compact }"
              >{{ primarySummary }}</span
            >
            <span
              v-if="compareSummary"
              class="ui-date-picker__compare-summary text-muted text-[0.625rem] w-max block text-nowrap"
              :class="{ 'leading-3': compact }"
            >
              {{ compareSeparator }}{{ compareSummary }}
            </span>
          </span>
        </span>
        <Icon
          class="ui-date-picker__chevron flex-none ms-auto text-muted transition-[transform] duration-150 ease-[ease]"
          :class="{ 'is-open rotate-180': isOpen }"
          name="chevron-down"
          :size="16"
        />
      </template>
    </button>

    <OverlayPortal>
      <FloatingLayer
        v-if="isOpen"
        v-model:element="floating"
        kind="popover"
        class="ui-date-picker__floating max-w-[var(--ui-floating-available-width,calc(100vw_-_0.75rem))] max-h-[var(--ui-floating-available-height,calc(100vh_-_0.75rem))] overflow-auto"
        :style="floatingStyles"
      >
        <FocusTrap :features="focusFeatures">
          <div
            :id="panelId"
            class="ui-date-picker__surface overflow-hidden border border-border rounded-lg outline-none bg-surface text-fg shadow-pop"
            role="dialog"
            :aria-label="triggerLabel"
          >
            <DatePickerPanel
              v-model="draft"
              embedded
              :presets="presets"
              :compare-modes="compareModes"
              :weekday-labels="weekdayLabels"
              :month-labels="monthLabels"
              :min="min"
              :max="max"
              :compare-toggle-label="compareToggleLabel"
              :start-input-label="startInputLabel"
              :end-input-label="endInputLabel"
              :compare-start-input-label="compareStartInputLabel"
              :compare-end-input-label="compareEndInputLabel"
              :start-placeholder="startPlaceholder"
              :end-placeholder="endPlaceholder"
              :compare-start-placeholder="compareStartPlaceholder"
              :compare-end-placeholder="compareEndPlaceholder"
              :hide-compare="hideCompare"
              :disabled="disabled"
              :hide-preset-list="hidePresetList"
              :hide-control-list="hideControlList"
              :hide-right-panel="hideRightPanel"
              :input-style="inputStyle"
              :compact="compact"
              :selectable-presets="selectablePresets"
              :calendar-height="calendarHeight"
              @change="handlePanelChange"
            />
            <div
              v-if="showFooter"
              class="ui-date-picker__actions flex justify-end gap-xs px-md pb-md"
            >
              <button
                v-if="showApplyAll && applyAllLabel"
                type="button"
                class="ui-date-picker__action ui-date-picker__action--all min-h-8 px-sm border-0 rounded-md text-xs cursor-pointer me-auto bg-info text-primary-fg focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-2"
                @click="commit('all', 'apply-all')"
              >
                {{ applyAllLabel }}
              </button>
              <button
                v-if="showControls"
                type="button"
                class="ui-date-picker__action ui-date-picker__action--reset min-h-8 px-sm border-0 rounded-md text-xs cursor-pointer bg-surface-sunken text-fg focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-2"
                @click="resetDraft"
              >
                {{ resetLabel }}
              </button>
              <button
                type="button"
                class="ui-date-picker__action ui-date-picker__action--apply min-h-8 px-sm border-0 rounded-md text-xs cursor-pointer bg-accent text-primary-fg focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-2"
                @click="commit('current', 'apply')"
              >
                {{ applyLabel }}
              </button>
            </div>
          </div>
        </FocusTrap>
      </FloatingLayer>
    </OverlayPortal>
  </div>
</template>
